import { create } from "zustand";
import { persist } from "zustand/middleware";

import { createOnRehydrateStorage, safeLocalStorage } from "@/stores/utils";

import type { PaymentSelectionState } from "./types";

/**
 * Client-side scratchpad for the checkout flow — the user's chosen
 * plan + billing cycle as they navigate the upgrade modal.
 *
 * This is the client half of the split that came out of
 * `usePaymentStore` when its server half (subscription status) moved
 * to TanStack Query (`useSubscription`). Persisting the selection in
 * localStorage means a user who opens the upgrade modal, picks
 * "Pro / annual", then bounces to another tab and comes back doesn't
 * lose their pick.
 *
 * Persist key migration note: we deliberately moved off the legacy
 * `"payment-storage"` key. That key previously held subscription
 * status (`hasActiveSubscription`, `currentPlan`, `billingCycle`,
 * `canceled`) which is now authoritative from the server — keeping
 * the same key would deserialize stale subscription data into the
 * new schema's typed fields. Users mid-checkout at the cutover lose
 * their selection; that's a one-tab-reload UX cost that's worth
 * paying for a clean store boundary.
 */
export const usePaymentSelectionStore = create<PaymentSelectionState>()(
  persist(
    (set) => ({
      _hasHydrated: false,
      _setHasHydrated: (state) => set({ _hasHydrated: state }),

      selectedPlan: null,
      selectedBillingCycle: null,

      setSelectedPlan: (plan) => set({ selectedPlan: plan }),
      setSelectedBillingCycle: (cycle) => set({ selectedBillingCycle: cycle }),

      resetSelection: () =>
        set({ selectedPlan: null, selectedBillingCycle: null }),
    }),
    {
      name: "payment-selection",
      storage: safeLocalStorage,
      partialize: (state) => ({
        selectedPlan: state.selectedPlan,
        selectedBillingCycle: state.selectedBillingCycle,
      }),
      onRehydrateStorage: createOnRehydrateStorage<PaymentSelectionState>(),
    },
  ),
);
